import type { ApiResponse } from "apisauce"

import { request } from "../../../../../services/api"
import { unwrapEnvelope } from "../../../../../utils/api/unwrap-envelope.ts"
import {
  filenameFromContentDisposition,
  saveBlob
} from "../../../../../utils/download-file.ts"

import {
  type ConflictSolverState,
  type LinkableRecord,
  type PlanReview,
  type PlanSheet,
  type RunSummary,
  type SowChatTurn,
  type SowDocument,
  type SowProjectTypeOption,
  type SowIssue,
  type SowSourceFile,
  type SowVersion,
  type SowWorkItem
} from "./services.types.ts"
import { SHEET_DPI } from "../measure/geometry.ts"

const BASE_URL = "/plans-estimate"

interface ErrorDetail {
  detail?: string
}

// Same envelope-unwrap pattern as the ReceiptFinancialLine section's own
// services.ts (EnvelopeJSONRenderer wraps every successful write as
// `{ status, details: { data } }`; GET responses are bare, and
// unwrapEnvelope is a no-op on an already-bare payload). A failure is read
// for its own `detail` message before falling back to the generic one.
async function unwrap<T>(
  requestPromise: Promise<ApiResponse<T, ErrorDetail>>,
  errorMessage: string
): Promise<T> {
  const response = await requestPromise
  if (response.ok && response.data) {
    return unwrapEnvelope<T>(response.data)
  }
  const detail = !response.ok ? response.data?.detail : undefined
  throw new Error(detail || errorMessage)
}

export function createDocument(title: string): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.post<SowDocument, ErrorDetail>(`${BASE_URL}/`, { title }),
    "Create failed"
  )
}

export function uploadPlanSource(
  id: string,
  file: File
): Promise<{ id: string; page_count: number }> {
  const form = new FormData()
  form.append("file", file)
  return unwrap<{ id: string; page_count: number }>(
    request.api.post<{ id: string; page_count: number }, ErrorDetail>(
      `${BASE_URL}/${id}/source/`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } }
    ),
    "Upload failed"
  )
}

export function updateDocument(
  id: string,
  data: Partial<SowDocument>
): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.patch<SowDocument, ErrorDetail>(`${BASE_URL}/${id}/`, data),
    "Update failed"
  )
}

export function generateDocument(id: string): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.post<SowDocument, ErrorDetail>(`${BASE_URL}/${id}/generate/`),
    "Generate failed"
  )
}

export function cancelDocument(id: string): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.post<SowDocument, ErrorDetail>(`${BASE_URL}/${id}/cancel/`),
    "Cancel failed"
  )
}

// Puts an estimate in the list's Archive, or brings it back to Active or Completed.
export function archiveDocument(id: string): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.post<SowDocument, ErrorDetail>(`${BASE_URL}/${id}/archive/`),
    "Archive failed"
  )
}

export function activateDocument(id: string): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.post<SowDocument, ErrorDetail>(`${BASE_URL}/${id}/activate/`),
    "Activate failed"
  )
}

// Not routed through `unwrap` — DELETE returns a bare 204 with no response
// body at all, so `unwrap`'s `response.data` truthiness check would read a
// genuinely successful delete as a failure.
export async function deleteDocument(id: string): Promise<void> {
  const response = await request.api.delete<undefined, ErrorDetail>(
    `${BASE_URL}/${id}/`
  )
  if (!response.ok) {
    throw new Error(response.data?.detail || "Delete failed")
  }
}

export async function downloadPdf(
  id: string,
  filename: string,
  versionId?: string
): Promise<void> {
  saveBlob(await fetchPreviewBlob(id, versionId), filename)
}

// Same blob-fetch pattern as `downloadDocument` above, but returns the Blob
// itself (for a `URL.createObjectURL` preview) instead of saving it to disk.
export async function fetchPreviewBlob(
  id: string,
  versionId?: string
): Promise<Blob> {
  const response = await request.api.get<Blob, ErrorDetail>(
    `${BASE_URL}/${id}/preview/`,
    versionId ? { version: versionId } : {},
    { responseType: "blob" }
  )
  if (!response.ok || !response.data) {
    let detail: string | undefined
    if (response.data instanceof Blob) {
      try {
        const text = await response.data.text()
        detail = JSON.parse(text)?.detail
      } catch {
        // ignore — fall through to the generic message
      }
    } else {
      detail = (response.data as ErrorDetail | undefined)?.detail
    }
    throw new Error(detail || "Preview failed")
  }
  return response.data
}

async function blobOrError(
  response: { ok: boolean; data?: unknown },
  fallback: string
): Promise<Blob> {
  if (response.ok && response.data instanceof Blob) return response.data
  let detail: string | undefined
  if (response.data instanceof Blob) {
    try {
      detail = JSON.parse(await response.data.text())?.detail
    } catch {
      // not JSON — fall through to the generic message
    }
  }
  throw new Error(detail || fallback)
}

// One page of the plan set as an image, for the sheet viewer.
export async function fetchSheetImage(id: string, page: number): Promise<Blob> {
  const response = await request.api.get<Blob, ErrorDetail>(
    `${BASE_URL}/${id}/sheet-image/`,
    { page, dpi: SHEET_DPI },
    { responseType: "blob" }
  )
  return blobOrError(response, "The sheet could not be drawn")
}

// One uploaded file (plan set or reference), for viewing in the app.
export async function fetchSourceBlob(
  id: string,
  sourceId: string
): Promise<Blob> {
  const response = await request.api.get<Blob, ErrorDetail>(
    `${BASE_URL}/${id}/sources/${sourceId}/`,
    {},
    { responseType: "blob" }
  )
  return blobOrError(response, "The file could not be opened")
}

export async function downloadSource(
  id: string,
  sourceId: string,
  fallbackName: string
): Promise<void> {
  const response = await request.api.get<Blob, ErrorDetail>(
    `${BASE_URL}/${id}/sources/${sourceId}/`,
    { download: 1 },
    { responseType: "blob" }
  )
  const blob = await blobOrError(response, "Download failed")
  saveBlob(
    blob,
    filenameFromContentDisposition(
      response.headers as Record<string, string> | undefined
    ) || fallbackName
  )
}

export async function deleteSource(
  id: string,
  sourceId: string
): Promise<void> {
  const response = await request.api.delete<unknown, ErrorDetail>(
    `${BASE_URL}/${id}/sources/${sourceId}/`
  )
  if (!response.ok)
    throw new Error(
      (response.data as ErrorDetail | undefined)?.detail ||
        "The file could not be deleted"
    )
}

// The uploaded plan set itself, for viewing in the app.
export async function fetchPlanBlob(id: string): Promise<Blob> {
  const response = await request.api.get<Blob, ErrorDetail>(
    `${BASE_URL}/${id}/plan/`,
    {},
    { responseType: "blob" }
  )
  if (!response.ok || !(response.data instanceof Blob)) {
    let detail: string | undefined
    if (response.data instanceof Blob) {
      try {
        detail = JSON.parse(await response.data.text())?.detail
      } catch {
        // not JSON — fall through to the generic message
      }
    }
    throw new Error(detail || "The plan could not be opened")
  }
  return response.data
}

export function getDocument(id: string): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.get<SowDocument, ErrorDetail>(`${BASE_URL}/${id}/`),
    "Fetch failed"
  )
}

// The list's categories: Active (not signed yet), Completed (signed), Archive.
export type InboxCategory = "active" | "completed" | "archive"

export interface InboxPage {
  results: SowDocument[]
  count: number
  // How many each category holds, as searched; absent on older servers.
  category_counts?: Record<InboxCategory, number>
}

export function listDocuments(params?: {
  page?: number
  page_size?: number
  // Finds an estimate by its name, its lead's or project's, a proposal
  // number or its plan file's name.
  q?: string
  category?: InboxCategory
}): Promise<InboxPage> {
  return unwrap<InboxPage>(
    request.api.get<InboxPage, ErrorDetail>(`${BASE_URL}/`, params),
    "List failed"
  )
}

interface ReviewResponse {
  sheets: PlanSheet[]
  issues: SowIssue[]
  folder_name: string
  files: SowSourceFile[]
  work_items: SowWorkItem[]
  // Optional so every pre-existing test fixture built before EZ-Chat shipped
  // stays valid without an update; ReviewScreen falls back to an empty list.
  chat_turns?: SowChatTurn[]
  // Same reason: optional so fixtures predating the version manager stay
  // valid; ReviewScreen falls back to an empty list.
  versions?: SowVersion[]
}

export function getLatestPlanReview(
  id: string
): Promise<{ plan_review: PlanReview | null }> {
  return unwrap<{ plan_review: PlanReview | null }>(
    request.api.get<{ plan_review: PlanReview | null }, ErrorDetail>(
      `${BASE_URL}/${id}/plan-review/latest/`
    ),
    "Could not load the plan review"
  )
}

export function startPlanReview(id: string): Promise<PlanReview> {
  return unwrap<PlanReview>(
    request.api.post<PlanReview, ErrorDetail>(`${BASE_URL}/${id}/plan-review/`),
    "Could not start the review"
  )
}

export function getReview(id: string): Promise<ReviewResponse> {
  return unwrap<ReviewResponse>(
    request.api.get<ReviewResponse, ErrorDetail>(`${BASE_URL}/${id}/review/`),
    "Review failed"
  )
}

interface LinkableRecordsResponse {
  count: number
  next: string | null
  previous: string | null
  results: LinkableRecord[]
}

export function searchLinkableRecords(params: {
  q?: string
  // Re-reads one record by id (the one a document is already linked to),
  // even after it stopped being active.
  id?: string
  entity_type?: "all" | "project" | "lead"
  page?: number
  page_size?: number
}): Promise<LinkableRecordsResponse> {
  return unwrap<LinkableRecordsResponse>(
    request.api.get<LinkableRecordsResponse, ErrorDetail>(
      `${BASE_URL}/linkable-records/`,
      params
    ),
    "Search failed"
  )
}

export function getConflictSolver(
  documentId: string
): Promise<ConflictSolverState> {
  return unwrap<ConflictSolverState>(
    request.api.get<ConflictSolverState, ErrorDetail>(
      `${BASE_URL}/${documentId}/conflict-solver/`
    ),
    "Could not load suggestions"
  )
}

// `workItems` narrows the pass to flagged scope items: all of them, or these.
export function startConflictSolver(
  documentId: string,
  workItems?: "all" | string[]
): Promise<ConflictSolverState> {
  return unwrap<ConflictSolverState>(
    request.api.post<ConflictSolverState, ErrorDetail>(
      `${BASE_URL}/${documentId}/conflict-solver/start/`,
      workItems ? { work_items: workItems } : undefined
    ),
    "Could not start the AI Conflict Solver"
  )
}

// Settles a flagged scope item without the AI: as written, or with `text`.
export function reviewWorkItem(
  documentId: string,
  workItemId: string,
  text?: string
): Promise<ConflictSolverState> {
  return unwrap<ConflictSolverState>(
    request.api.post<ConflictSolverState, ErrorDetail>(
      `${BASE_URL}/${documentId}/work-items/${workItemId}/review/`,
      text === undefined ? {} : { text }
    ),
    "Could not update the scope item"
  )
}

export function adoptSuggestion(
  documentId: string,
  suggestionId: string,
  text: string
): Promise<ConflictSolverState> {
  return unwrap<ConflictSolverState>(
    request.api.post<ConflictSolverState, ErrorDetail>(
      `${BASE_URL}/${documentId}/conflict-solver/suggestions/${suggestionId}/adopt/`,
      { text }
    ),
    "Could not adopt the suggestion"
  )
}

export function ignoreSuggestion(
  documentId: string,
  suggestionId: string
): Promise<ConflictSolverState> {
  return unwrap<ConflictSolverState>(
    request.api.post<ConflictSolverState, ErrorDetail>(
      `${BASE_URL}/${documentId}/conflict-solver/suggestions/${suggestionId}/ignore/`
    ),
    "Could not ignore the suggestion"
  )
}

export function getSowProjectTypes(): Promise<SowProjectTypeOption[]> {
  return unwrap<SowProjectTypeOption[]>(
    request.api.get<SowProjectTypeOption[], ErrorDetail>(
      `${BASE_URL}/project-types/`
    ),
    "Could not load project types"
  )
}

// How the AI service last answered: out of credits or busy, or fine (null).
export interface AiServiceState {
  code: "ai_out_of_credits" | "ai_busy" | null
  at: string | null
}

export function getAiStatus(): Promise<AiServiceState> {
  return unwrap<AiServiceState>(
    request.api.get<AiServiceState, ErrorDetail>(`${BASE_URL}/ai-status/`),
    "Could not read the AI service's state"
  )
}

export function getRunSummary(id: string): Promise<RunSummary> {
  return unwrap<RunSummary>(
    request.api.get<RunSummary, ErrorDetail>(`${BASE_URL}/${id}/run-summary/`),
    "Could not load the run"
  )
}

// After the job details change: new requests become lines and AI prices are
// taken again. Never reads the plans.
export function updateEstimateFromDetails(id: string): Promise<SowDocument> {
  return unwrap<SowDocument>(
    request.api.post<SowDocument, ErrorDetail>(
      `${BASE_URL}/${id}/estimate/update/`
    ),
    "Could not update the estimate"
  )
}
